/**
 * React Query hooks for the Onboarding Pipeline screen.
 *
 * Mutations invalidate all three list queries (overview, funnel,
 * prospects) so the KPI badges stay in sync with the table after
 * an add / pause / resume.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateProspectInput,
  createProspect,
  fetchAgenticConfig,
  fetchPipelineFunnel,
  fetchPipelineOverview,
  fetchProspectDetail,
  fetchProspects,
  pauseProspect,
  ProspectListParams,
  resumeProspect,
  runProspecting,
  runSend,
  updateAgenticConfig,
  UpdateAgenticConfigInput
} from '@/api/pipeline';

const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['pipelineOverview'] });
  qc.invalidateQueries({ queryKey: ['pipelineFunnel'] });
  qc.invalidateQueries({ queryKey: ['pipelineProspects'] });
};

export function usePipelineOverview() {
  return useQuery({
    queryKey: ['pipelineOverview'],
    queryFn: fetchPipelineOverview,
    refetchInterval: 120_000
  });
}

export function usePipelineFunnel() {
  return useQuery({
    queryKey: ['pipelineFunnel'],
    queryFn: fetchPipelineFunnel,
    refetchInterval: 120_000
  });
}

export function usePipelineProspects(
  params: ProspectListParams,
  enabled = true
) {
  return useQuery({
    queryKey: ['pipelineProspects', params],
    queryFn: () => fetchProspects(params),
    enabled,
    refetchInterval: 120_000,
    placeholderData: prev => prev
  });
}

export function useProspectDetail(id: string | null) {
  return useQuery({
    queryKey: ['prospectDetail', id],
    queryFn: () => fetchProspectDetail(id!),
    enabled: Boolean(id)
  });
}

export function useCreateProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProspectInput) => createProspect(input),
    onSuccess: () => invalidateAll(qc)
  });
}

export function usePauseProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason?: string }) =>
      pauseProspect(input.id, input.reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['prospectDetail', vars.id] });
      qc.invalidateQueries({ queryKey: ['pipelineProspects'] });
    }
  });
}

export function useResumeProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) => resumeProspect(input.id),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['prospectDetail', vars.id] });
      qc.invalidateQueries({ queryKey: ['pipelineProspects'] });
    }
  });
}

// ─── Agentic controls (Outreach tab) ──────────────────────────────────────

export function useAgenticConfig() {
  return useQuery({
    queryKey: ['agenticConfig'],
    queryFn: fetchAgenticConfig,
    refetchInterval: 60_000
  });
}

export function useUpdateAgenticConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAgenticConfigInput) => updateAgenticConfig(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agenticConfig'] })
  });
}

export function useRunProspecting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dryRun?: boolean) => runProspecting(dryRun),
    onSuccess: () => invalidateAll(qc)
  });
}

export function useRunSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => runSend(),
    onSuccess: () => invalidateAll(qc)
  });
}
